import { useMemo, useState } from 'react'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import { MessageChannelOptionsEnum, type CountryCode } from '@sevenrooms/domain'
import { ReservationWidget } from '@sevenrooms/domain/constants'
import { z, type ZodSchema, useForm } from '@sevenrooms/form'
import { useLocales, getCountryData } from '@sevenrooms/locales'
import { DateOnly } from '@sevenrooms/timepiece'
import {
  Form,
  FormTimeRangePicker,
  Label,
  type TimeRangePickerForm,
  useTimeRangePickerForm,
  Button,
  Checkbox,
} from '@sevenrooms/ui-kit/form'
import { useMaxWidthBreakpoint } from '@sevenrooms/ui-kit/hooks'
import { Icon } from '@sevenrooms/ui-kit/icons'
import { VStack, HStack, Box, Modal, ModalBody, ModalFooter, ModalTitle, Grid, DividerLine } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import { AgreementTerm, AgreementDetailModal } from '../../../components/AgreementTerms/AgreementTerm'
import { EmailAddressForm } from '../../../components/EmailAddressForm/EmailAddressForm'
import { FirstNameForm } from '../../../components/FirstNameForm/FirstNameForm'
import { LastNameForm } from '../../../components/LastNameForm/LastNameForm'
import { PhoneNumberForm } from '../../../components/PhoneNumberForm/PhoneNumberForm'
import { useCreateRequestMutation } from '../../../domain/request'
import { useVenue, useVenueGroup, useWidgetSettings } from '../../../hooks'
import { reservationWidgetMessages } from '../../../reservationWidgetMessages'
import { useModals, useReservationFormState } from '../../../store'
import { priorityAlertSubmitted } from '../../../utils'
import { BackModalHeader } from '../BackModalHeader'

export type CreatePriorityAlertForm = ZodSchema<typeof useCreatePriorityAlertFormSchema>
export const useCreatePriorityAlertFormSchema = () => {
  const { formatMessage } = useLocales()
  const { startOfDayTime } = useVenue()
  const timeRange = useTimeRangePickerForm({ startOfDayTime, nullable: false, allowSameTime: true })
  const requiredErrorMessage = formatMessage(reservationWidgetMessages.resWidgetErrorsFieldRequired)
  const invalidErrorMessage = formatMessage(reservationWidgetMessages.resWidgetErrorsFieldInvalid)

  return useMemo(
    () =>
      z
        .object({
          emailAddress: z.string().min(1, { message: requiredErrorMessage }).email({ message: invalidErrorMessage }),
          firstName: z.string().min(1, { message: requiredErrorMessage }),
          lastName: z.string().min(1, { message: requiredErrorMessage }),
          phoneNumber: z.string().min(1, { message: requiredErrorMessage }),
          agreeToTextMessages: z.boolean(),
          phoneCountryCode: z.custom<CountryCode>(),
          phoneDialCode: z.string().min(1),
          preferredMessagingChannel: z.nativeEnum(MessageChannelOptionsEnum).optional(),
          timeRange,
          agreedVenueSpecificMarketingOnForRequest: z.boolean(),
          agreedVenueGroupMarketingOnForRequest: z.boolean(),
          agreedTailoredCommunicationOnForRequest: z.boolean(),
          agreedVenueSmsMarketingOnForRequest: z.boolean(),
        })
        .superRefine((val, ctx) => {
          if (!isPossiblePhoneNumber(val.phoneNumber)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: invalidErrorMessage,
              path: ['phoneNumber'],
            })
          }
        }),
    [invalidErrorMessage, requiredErrorMessage, timeRange]
  )
}

interface CreatePriorityAlertProps {
  onClose: () => void
  onBack?: () => void
  validRequestTimes: string[]
  partySize: number
  startDate: string
}

export function CreatePriorityAlert({ startDate, partySize, onClose, onBack, validRequestTimes }: CreatePriorityAlertProps) {
  const { id: venueId, urlKey, isSmsMarketingEnabled, name: venueName } = useVenue()
  const widgetSettings = useWidgetSettings()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { formatMessage } = useLocales()
  const isMobile = useMaxWidthBreakpoint('s')
  const [createRequest, { isLoading }] = useCreateRequestMutation({
    fixedCacheKey: 'reservation-create-request',
  })
  const { lockedFields } = useVenueGroup()

  const { formState, updateFormState } = useReservationFormState()
  const { defaultMessageChannel, isWhatsappEnabled, isGuestChannelPreferenceSelectionDisabled } = useVenue()
  const { phoneCountryCode, firstName, lastName, emailAddress, phoneNumber, startTime, selectedExperience } = formState
  const { showModal, showErrorModal } = useModals()

  const phoneDialCode = getCountryData(phoneCountryCode)?.dialCode ?? '1'
  const priorityAlertFormSchema = useCreatePriorityAlertFormSchema()

  const timeRange: TimeRangePickerForm =
    startTime === ReservationWidget.AllTimesOption
      ? {
          startTime: null,
          endTime: null,
        }
      : {
          startTime: validRequestTimes[0] || null,
          endTime: validRequestTimes[validRequestTimes.length - 1] || null,
        }

  const agreedVenueSpecificMarketingOnForRequest = widgetSettings.venueSpecificMarketingOnForRequest
    ? widgetSettings.isVenueSpecificMarketingPolicyDefaultOnForRequest
    : false
  const agreedVenueGroupMarketingOnForRequest = widgetSettings.venueGroupMarketingOnForRequest
    ? widgetSettings.isVenueGroupMarketingPolicyDefaultOnForRequest
    : false
  const agreedTailoredCommunicationOnForRequest = widgetSettings.tailoredCommunicationOnForRequest
    ? widgetSettings.isTailoredCommunicationPolicyDefaultOnForRequest
    : false
  const agreedVenueSmsMarketingOnForRequest =
    isSmsMarketingEnabled && widgetSettings.venueSmsMarketingOnForRequest
      ? widgetSettings.isVenueSmsMarketingPolicyDefaultOnForRequest
      : false

  const form = useForm(priorityAlertFormSchema, {
    mode: 'onSubmit',
    defaultValues: {
      agreeToTextMessages: widgetSettings.reservationSmsOptInOn,
      preferredMessagingChannel: isWhatsappEnabled ? defaultMessageChannel : undefined,
      firstName,
      lastName,
      emailAddress,
      phoneNumber,
      phoneCountryCode,
      phoneDialCode,
      timeRange,
      agreedVenueSpecificMarketingOnForRequest,
      agreedVenueGroupMarketingOnForRequest,
      agreedTailoredCommunicationOnForRequest,
      agreedVenueSmsMarketingOnForRequest,
    },
  })
  const { field, getValues, setError } = form
  const disabled = isSubmitting || isLoading

  const saveOnClose = () => {
    updateFormState({ ...formState, ...getValues() })
    onClose()
  }

  const onSubmit = async (data: CreatePriorityAlertForm) => {
    try {
      setIsSubmitting(true)
      updateFormState({ ...formState, ...data })
      const timeRange = data.timeRange as { startTime: string; endTime: string }
      await createRequest({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.emailAddress,
        phoneNumber: data.phoneNumber,
        countryCode: data.phoneCountryCode,
        phoneDialCode: data.phoneDialCode,
        preferredLanguageCode: formState.preferredLanguage,
        preferredMessagingChannel: isWhatsappEnabled ? data.preferredMessagingChannel : undefined,
        date: startDate,
        startTime: timeRange.startTime,
        endTime: timeRange.endTime,
        partySize,
        requestClass: 'table',
        requestSmsOptIn: data.agreeToTextMessages,
        venueId: selectedExperience?.venueId || venueId,
        // eslint-disable-next-line no-warning-comments
        // TODO GX-2914
        channel: 'SEVENROOMS_WIDGET',
        trackingSlug: formState.trackingSlug,
        clientId: undefined,
        experienceId: undefined,
        referralId: undefined,
        venueSpecificMarketingOptIn: widgetSettings.venueSpecificMarketingOnForRequest
          ? data.agreedVenueSpecificMarketingOnForRequest
          : undefined,
        venueGroupMarketingOptIn: widgetSettings.venueGroupMarketingOnForRequest ? data.agreedVenueGroupMarketingOnForRequest : undefined,
        tailoredCommunicationOptIn: widgetSettings.tailoredCommunicationOnForRequest
          ? data.agreedTailoredCommunicationOnForRequest
          : undefined,
        venueSmsMarketingOptIn: widgetSettings.venueSmsMarketingOnForRequest ? data.agreedVenueSmsMarketingOnForRequest : undefined,
      }).unwrap()
      showModal('createPriorityAlertSuccess')
      priorityAlertSubmitted(urlKey, startDate, partySize, timeRange.startTime, timeRange.endTime)
    } catch (error) {
      if (typeof error === 'string') {
        setError('timeRange', {
          message: formatMessage(reservationWidgetMessages.resWidgetPriorityAlertsAlreadyExistsError),
        })
      } else {
        showErrorModal()
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInvalid = (data: unknown) => {
    // Remove before mvp, useful for debugging for now.
    // eslint-disable-next-line no-console
    console.log(data)
  }

  const formattedStartDate = DateOnly.fromSafe(startDate)?.formatNYearFMonthNDayFWeek()
  return (
    <Form onSubmit={onSubmit} onInvalid={handleInvalid} {...form}>
      <Modal
        data-test="sr-create-priority-alert-modal"
        width="100%"
        ariaLabel={formatMessage(reservationWidgetMessages.resWidgetPriorityAlertsSetAlertTitle)}
      >
        <BackModalHeader onClose={saveOnClose} onBack={onBack}>
          <VStack spacing="s">
            <ModalTitle
              data-test="sr-create-priority-alert-modal-title"
              title={formatMessage(reservationWidgetMessages.resWidgetPriorityAlertsDetailsHeader)}
            />
            <Text>{formatMessage(reservationWidgetMessages.resWidgetPriorityAlertsHeader)}</Text>
          </VStack>
        </BackModalHeader>
        <ModalBody>
          <VStack mb="m" mt="m" spacing="m">
            <HStack spacing="s" alignItems="center">
              <Icon name="GX-calendar-range" size="lg" />
              <Text data-test="sr-create-priority-alert-modal-date">{formattedStartDate}</Text>
            </HStack>
            <HStack spacing="s" alignItems="center">
              <Icon name="GX-account-multiple" size="lg" />
              <Text data-test="sr-create-priority-alert-modal-party-size">
                {`${partySize} ${
                  partySize === 1
                    ? formatMessage(reservationWidgetMessages.commonGuestLabel)
                    : formatMessage(reservationWidgetMessages.commonGuestsLabel)
                }`}
              </Text>
            </HStack>
          </VStack>
          <VStack spacing="m">
            <Label primary={`${formatMessage(reservationWidgetMessages.resWidgetPriorityAlertsReservationTimeBetween)}*`}>
              <FormTimeRangePicker
                data-test="sr-create-priority-alert-modal-time-range"
                field={field.prop('timeRange')}
                disabled={disabled}
                timeSlots={validRequestTimes}
                selectProps={{ withNativeSupport: true, zIndexLayer: 'modal' }}
              />
            </Label>
            <DividerLine ml="none" mr="none" />
            <Grid
              gridAutoColumns="auto auto"
              gridTemplateColumns={`repeat(${isMobile ? 1 : 2}, minmax(0, 1fr))`}
              gap="m"
              alignItems="start"
            >
              <FirstNameForm firstNameField={field.prop('firstName')} disabled={lockedFields?.firstName || disabled} />
              <LastNameForm lastNameField={field.prop('lastName')} disabled={lockedFields?.lastName || disabled} />
              <EmailAddressForm emailAddressField={field.prop('emailAddress')} disabled={lockedFields?.email || disabled} />
              <PhoneNumberForm
                phoneNumberField={field.prop('phoneNumber')}
                disabled={lockedFields?.phoneNumber ?? disabled}
                phoneCountryCodeField={field.prop('phoneCountryCode')}
                phoneDialCodeField={field.prop('phoneDialCode')}
              />
            </Grid>
            {isSmsMarketingEnabled && widgetSettings.venueSmsMarketingOnForRequest && (
              <Checkbox
                field={field.prop('agreedVenueSmsMarketingOnForRequest')}
                data-test="agreedVenueSmsMarketingOnForRequest"
                checked={widgetSettings.isVenueSmsMarketingPolicyDefaultOnForRequest}
                extra={
                  <AgreementDetailModal
                    title={formatMessage(reservationWidgetMessages.resWidgetMarketingPolicyHeader)}
                    content={formatMessage(
                      reservationWidgetMessages.policyVenueSpecificSmsMarketing,
                      { venue: venueName },
                      { ignoreTag: true }
                    )}
                  />
                }
              >
                {formatMessage(reservationWidgetMessages.resWidgetCheckoutVenueSmsOptIn, { venue: venueName })}
              </Checkbox>
            )}
            {widgetSettings.venueSpecificMarketingOnForRequest && (
              <Checkbox
                field={field.prop('agreedVenueSpecificMarketingOnForRequest')}
                data-test="agreedVenueSpecificMarketingOnForRequest"
                checked={widgetSettings.isVenueSpecificMarketingPolicyDefaultOnForRequest}
                extra={
                  <AgreementDetailModal
                    title={formatMessage(reservationWidgetMessages.resWidgetMarketingPolicyHeader)}
                    content={formatMessage(
                      reservationWidgetMessages.policyVenueSpecificMarketing,
                      { venue: venueName },
                      { ignoreTag: true }
                    )}
                  />
                }
              >
                {formatMessage(reservationWidgetMessages.resWidgetCheckoutVenueOptIn, { venue: venueName })}
              </Checkbox>
            )}
            {widgetSettings.venueGroupMarketingOnForRequest && (
              <Checkbox
                field={field.prop('agreedVenueGroupMarketingOnForRequest')}
                data-test="agreedVenueGroupMarketingOnForRequest"
                checked={widgetSettings.isVenueGroupMarketingPolicyDefaultOnForRequest}
                extra={
                  <AgreementDetailModal
                    title={formatMessage(reservationWidgetMessages.resWidgetMarketingPolicyHeader)}
                    content={formatMessage(reservationWidgetMessages.policyVenueGroupMarketing, { venue: venueName }, { ignoreTag: true })}
                  />
                }
              >
                {formatMessage(reservationWidgetMessages.resWidgetCheckoutVenueGroupOptIn)}
              </Checkbox>
            )}
            {widgetSettings.tailoredCommunicationOnForRequest && (
              <Checkbox
                field={field.prop('agreedTailoredCommunicationOnForRequest')}
                data-test="agreedTailoredCommunicationOnForRequest"
                checked={widgetSettings.isTailoredCommunicationPolicyDefaultOnForRequest}
              >
                {formatMessage(reservationWidgetMessages.textTailoredCommunicationOptInLabel)}
              </Checkbox>
            )}
            {widgetSettings.displayReservationSmsOptIn && (
              <AgreementTerm
                disabled={disabled}
                agreement={{
                  dataTest: 'sr-create-priority-alert-modal-sms-agreement',
                  term: formatMessage(reservationWidgetMessages.policyUsTwilioSmsOptIn),
                  field: field.prop('agreeToTextMessages'),
                  subOptions:
                    isWhatsappEnabled && !isGuestChannelPreferenceSelectionDisabled
                      ? {
                          field: field.prop('preferredMessagingChannel'),
                          options: [
                            {
                              label: formatMessage(reservationWidgetMessages.resWidgetPreferredChannelWhatsAppLabel),
                              value: MessageChannelOptionsEnum.WHATSAPP,
                            },
                            {
                              label: formatMessage(reservationWidgetMessages.resWidgetPreferredChannelSmsLabel),
                              value: MessageChannelOptionsEnum.SMS_MMS,
                            },
                          ],
                        }
                      : undefined,
                }}
              />
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            data-test="alert-me-button"
            disabled={disabled}
            data-test-id="sr-create-priority-alert-modal-set-alert"
            type="submit"
            fullWidth
            size="l"
          >
            <Icon name="GX-bell-ring" size="lg" />
            <Box ml="xs">{formatMessage(reservationWidgetMessages.resWidgetPriorityAlertsSetAlertButton)}</Box>
          </Button>
        </ModalFooter>
      </Modal>
    </Form>
  )
}
